import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  Divider,
  Card,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MetricType } from "../../../types/dashboard";
import { formatCurrencyShort } from "../../../utils/format";
import { useDashboard } from "../../../contexts/DashboardContext";
import { MetricBarsSkeleton } from "./Skeletons";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

interface MetricsByCellProps {
  metricType: MetricType;
  title: string;
  color: string;
  allowedCellIds?: string[];
}

const StyledPaper = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 282,
}));

const ChartTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

const MetricRow = styled(Box)({
  display: "flex",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  paddingRight: 8,
  position: "relative",
});

const MetricName = styled(Typography)({
  fontSize: "0.75rem",
  color: "#3E3E3E",
  width: 80,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const MetricValue = styled(Typography)({
  fontSize: "0.675rem",
  color: "#222222",
  minWidth: 60,
  textAlign: "right",
});

const StyledLinearProgress = styled(LinearProgress)({
  height: 16,
  borderRadius: 4,
  flex: 1,
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
  },
});

export const MetricsByCell = ({
  metricType,
  title,
  color,
  allowedCellIds,
}: MetricsByCellProps) => {
  const {
    initialEventControlRankings,
    filteredEventControlRankings,
    loading,
    currentFilters,
    toggleCellFilter,
    hasFilters,
  } = useDashboard();

  const rankingKey = metricType === MetricType.BILLING ? "billingByCell" : "revenueByCell";
  // Usa initial como base estável da lista; cai para filtered se o load inicial falhou
  const effectiveInitial = initialEventControlRankings ?? filteredEventControlRankings;
  const initialData = effectiveInitial?.[rankingKey] ?? [];
  const filteredData = hasFilters
    ? (filteredEventControlRankings?.[rankingKey] ?? [])
    : initialData;

  const combinedData = initialData.map((item) => {
    const filtered = filteredData.find((f) => f.id === item.id);
    return {
      cellId: item.id,
      cellName: item.name,
      filteredValue: filtered?.value ?? 0,
      filteredPercentage: filtered?.percentage ?? 0,
    };
  });

  const filteredCombinedData = allowedCellIds?.length
    ? combinedData.filter((i) => allowedCellIds.includes(i.cellId))
    : combinedData;

  return (
    <StyledPaper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChartTitle>{title}</ChartTitle>
          <DashboardSourceInfo context={title} />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <MetricBarsSkeleton />
      ) : (
        <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
          {filteredCombinedData
            .sort((a, b) => b.filteredValue - a.filteredValue)
            .map((item) => (
              <MetricRow
                key={item.cellId}
                onClick={() => toggleCellFilter(item.cellId)}
                sx={{
                  cursor: "pointer",
                  bgcolor:
                    currentFilters.cellId === item.cellId
                      ? `${color}10`
                      : "transparent",
                  "&:hover": {
                    bgcolor: `${color}20`,
                  },
                }}
              >
                <MetricName>{item.cellName}</MetricName>

                <Box position="relative" flex={1}>
                  <StyledLinearProgress
                    variant="determinate"
                    value={item.filteredPercentage}
                    sx={{
                      backgroundColor: hasFilters
                        ? `${color}20`
                        : "transparent",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: color,
                      },
                    }}
                  />
                </Box>

                <MetricValue>
                  {formatCurrencyShort(item.filteredValue)}
                </MetricValue>
              </MetricRow>
            ))}
        </Box>
      )}
    </StyledPaper>
  );
};
